import * as React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Edit2, Check, X } from "lucide-react";

interface PostTitleSectionProps {
  value: string;
  onChange: (value: string) => void;
  permalink: string;
  code?: string;
  onCodeChange?: (code: string) => void;
  onCodeSave?: () => Promise<void>;
  isCodeSaving?: boolean;
  error?: string;
  required?: boolean;
  prefix?: string; // e.g., "/agent/" or "/blog/"
  label?: string; // e.g., "Agent Title" or "Tiêu đề bài viết"
  placeholder?: string;
}

export function PostTitleSection({
  value,
  onChange,
  permalink,
  code,
  onCodeChange,
  onCodeSave,
  isCodeSaving = false,
  error,
  required = false,
  prefix = "/agent/",
  label = "Agent Title",
  placeholder = "Enter your agent title here..."
}: PostTitleSectionProps) {
  const [isEditingPermalink, setIsEditingPermalink] = React.useState(false);
  const [editedCode, setEditedCode] = React.useState(code || "");
  const [permalinkError, setPermalinkError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setEditedCode(code || "");
  }, [code]);

  const handleEditClick = () => {
    setIsEditingPermalink(true);
    setEditedCode(code || "");
    setPermalinkError(null);
  };

  const handleCancel = () => {
    setIsEditingPermalink(false);
    setEditedCode(code || "");
    setPermalinkError(null);
  };

  const handleSave = async () => {
    // Validate code
    if (!editedCode.trim()) {
      setPermalinkError("Permalink không được để trống");
      return;
    }

    // Validate format (only lowercase letters, numbers, and hyphens)
    const codePattern = /^[a-z0-9-]+$/;
    if (!codePattern.test(editedCode.trim())) {
      setPermalinkError("Permalink chỉ được chứa chữ thường, số và dấu gạch ngang");
      return;
    }

    setPermalinkError(null);

    if (onCodeChange) {
      onCodeChange(editedCode.trim());
    }

    if (onCodeSave) {
      try {
        await onCodeSave();
        setIsEditingPermalink(false);
      } catch (error) {
        setPermalinkError(error instanceof Error ? error.message : "Không thể lưu permalink");
      }
    } else {
      setIsEditingPermalink(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  // Extract code from permalink (remove prefix)
  const displayCode = permalink.replace(new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`), "");

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`text-lg font-medium ${
            error ? "border-red-500" : "border-gray-300"
          }`}
        />
        {error && (
          <p className="text-sm text-red-500 mt-1">{error}</p>
        )}
        <div className="mt-2">
          <div className="flex items-center gap-2">
            <p className="text-sm text-gray-500">
              Permalink: <span className="text-primary">{prefix}</span>
            </p>
            {isEditingPermalink ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={editedCode}
                  onChange={(e) => {
                    const newValue = e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, "-")
                      .replace(/-+/g, "-")
                      .replace(/^-+|-+$/g, "");
                    setEditedCode(newValue);
                    setPermalinkError(null);
                  }}
                  onKeyDown={handleKeyDown}
                  className={`flex-1 max-w-xs text-sm ${
                    permalinkError ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="permalink-slug"
                  disabled={isCodeSaving}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSave}
                  disabled={isCodeSaving}
                  className="h-8 px-2"
                >
                  <Check className="h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isCodeSaving}
                  className="h-8 px-2"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <>
                <span className="text-primary">{displayCode}</span>
                {code && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleEditClick}
                    className="h-6 px-2 text-gray-400 hover:text-gray-600"
                  >
                    <Edit2 className="h-3 w-3" />
                  </Button>
                )}
              </>
            )}
          </div>
          {permalinkError && (
            <p className="text-sm text-red-500 mt-1">{permalinkError}</p>
          )}
        </div>
      </div>
    </div>
  );
}